
export const cleanHtmlTags = (text: string): string => {
  console.log('Cleaning HTML from text:', text);
  const cleaned = text
    .replace(/<br\s*\/?>/gi, ' ')
    .replace(/<div[^>]*id='[^']*'>(.*?)<\/div>/gi, '$1')
    .replace(/<div[^>]*>(.*?)<\/div>/gi, '$1')
    .replace(/<[^>]+>/g, '')
    .replace(/&nbsp;/g, ' ')
    .replace(/\s{2,}/g, ' ')
    .replace(/["]/g, '"')
    .trim();
  console.log('Cleaned text:', cleaned);
  return cleaned;
};

export const validateQuestionQuality = (question: string, options: string[]): { valid: boolean; reason?: string } => {
  // Validate question has enough context
  if (question.length < 15) {
    return { valid: false, reason: 'Question is too short to be meaningful' };
  }
  
  // Check if question has proper grammar (ends with question mark)
  if (!question.includes('?') && !question.endsWith('.')) {
    return { valid: false, reason: 'Question format is invalid (missing question mark or proper ending)' };
  }
  
  // Check for incomplete context indicators
  const incompleteKeywords = ['refer to', 'above', 'following', 'see the', 'in the figure', 'shown'];
  for (const keyword of incompleteKeywords) {
    if (question.toLowerCase().includes(keyword) && !question.includes('(') && !question.includes('{')) {
      return { valid: false, reason: `Question refers to missing context: ${keyword}` };
    }
  }
  
  // Updated options validity - allow short options but they must have at least one character and not be just whitespace
  if (options.some(opt => !opt.trim() || opt.trim().length === 0)) {
    return { valid: false, reason: 'Some options are empty or contain only whitespace' };
  }
  
  if (new Set(options).size !== options.length) {
    return { valid: false, reason: 'Duplicate options found' };
  }
  
  return { valid: true };
};

export const parseAnkiQuestion = (line: string, subject: string) => {
  console.log('Starting to parse line:', line);
  
  const parts = line.split('\t');
  
  if (parts.length < 2) {
    console.error('Invalid line format - not enough parts:', line);
    return null;
  }

  // Parse question text and options
  const questionPart = parts[0];
  const answerPart = parts[1];

  console.log('Question part:', questionPart);
  console.log('Answer part:', answerPart);

  // First, extract options section
  const optionsMatch = questionPart.match(/Options:<br>([^]*)/i);
  if (!optionsMatch) {
    console.error('Could not find options section');
    return null;
  }

  // Extract main question (everything before Options:)
  const mainQuestion = questionPart.split(/Options:<br>/i)[0];
  const optionsText = optionsMatch[1];

  console.log('Main question:', mainQuestion);
  console.log('Options text:', optionsText);

  let question = cleanHtmlTags(mainQuestion);

  // Extract options using regex
  const options: string[] = [];
  const optionRegex = /([A-D])\.\s*([^]*?)(?=(?:\s*[A-D]\.|$))/g;
  let match;
  
  while ((match = optionRegex.exec(optionsText)) !== null) {
    console.log('Found option match:', match);
    const optionText = cleanHtmlTags(match[2]);
    options.push(optionText);
  }

  console.log('Extracted options:', options);

  // Validate options
  if (options.length !== 4) {
    console.error('Invalid number of options:', options.length);
    console.error('Found options:', options);
    return null;
  }
  
  // Validate question quality
  const qualityCheck = validateQuestionQuality(question, options);
  if (!qualityCheck.valid) {
    console.error(`Question quality check failed: ${qualityCheck.reason}`);
    console.error('Question:', question);
    return null;
  }

  // Clean and extract the correct answer
  const answer = cleanHtmlTags(answerPart);
  console.log('Cleaned answer:', answer);
  
  // Find correct answer index
  let correctAnswerIndex = options.findIndex(opt => 
    opt.toLowerCase().trim() === answer.toLowerCase().trim()
  );

  console.log('Initial correct answer index:', correctAnswerIndex);

  if (correctAnswerIndex === -1) {
    // Try a more flexible match
    correctAnswerIndex = options.findIndex(opt => {
      const optLower = opt.toLowerCase().trim();
      const ansLower = answer.toLowerCase().trim();
      const match = optLower.includes(ansLower) || ansLower.includes(optLower);
      console.log('Checking option:', opt, 'against answer:', answer, 'Match:', match);
      return match;
    });
  }

  if (correctAnswerIndex === -1) {
    console.error('Could not determine correct answer');
    console.error('Answer:', answer);
    console.error('Available options:', options);
    return null;
  }

  console.log('Final correct answer index:', correctAnswerIndex);

  // Determine difficulty based on specific rules
  let difficulty = 'easy'; // Default
  
  // Rule 1: Question length > 100 characters = hard
  if (question.length > 100) {
    difficulty = 'hard';
  } 
  // Rule 2: Any option length > 60 characters = medium (if not already hard)
  else if (
    options[0].length > 60 ||
    options[1].length > 60 ||
    options[2].length > 60 ||
    options[3].length > 60
  ) {
    difficulty = 'medium';
  }
  // Otherwise it stays 'easy'
  
  console.log(`Determined difficulty: ${difficulty} based on question and option lengths`);

  const questionKey = `${question}-${answer}`;
  
  const result = {
    questionKey,
    data: {
      "Category": subject,
      "Question": question,
      "Option A": options[0],
      "Option B": options[1],
      "Option C": options[2],
      "Option D": options[3],
      "Correct Answer": options[correctAnswerIndex],
      "Difficulty": difficulty, // Apply our calculated difficulty
      created_at: new Date().toISOString(),
      status: 'verified' as const,
      source: 'anki',
      question_type: 'mcq' as const,
      metadata: {
        quality_score: 1, // Default quality score
        imported_from: 'anki',
        has_been_reviewed: false
      }
    }
  };

  console.log('Successfully parsed question:', result);
  return result;
};
